import { useMemo, useRef, useState, useEffect } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { resolveImageUrl } from '@/shared/utils/imageUtils';

// Lazy Image Component with IntersectionObserver
function LazyImage({
  src,
  dataSrc,
  alt,
  className,
}: {
  src: string;
  dataSrc?: string;
  alt: string;
  className: string;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageSrc, setImageSrc] = useState(src);

  useEffect(() => {
    if (!dataSrc || !imgRef.current) return;

    const img = imgRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(resolveImageUrl(dataSrc));
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    observer.observe(img);

    return () => observer.disconnect();
  }, [dataSrc]);

  return (
    <img
      ref={imgRef}
      className={className}
      src={resolveImageUrl(imageSrc)}
      alt={alt}
      loading="lazy"
    />
  );
}

interface ClipCardItem {
  title?: string;
  description?: string;
  imageUrl?: string;
  name?: string;
  age?: number;
  testimonial?: string;
  link?: string;
  rotation?: number;
  lazyload?: string;
  clipImageUrl?: string;
  clipPosition?: string;
  clipRotation?: string;
  shouldRefresh?: boolean;
}

interface ClipCardsSectionProps {
  data: Record<string, unknown> & {
    title?: string;
    hashtag?: string;
    items?: ClipCardItem[];
    blankImageUrl?: string;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function ClipCardsSection({
  data,
  sectionKey,
  eventLabel,
  priority,
}: ClipCardsSectionProps) {
  const items = useMemo(() => (data.items || []) as ClipCardItem[], [data.items]);
  const titleRaw = data.title || 'Customer Testimonials';
  const hashtag = data.hashtag || '#BlueStoneAndMe';
  const blankImageUrl = data.blankImageUrl as string | undefined;

  // Split title by <br> tag - first part is title, second part is subtext
  const titleParts = useMemo(() => {
    let titleString = '';
    if (Array.isArray(titleRaw)) {
      titleString = titleRaw.join(' ');
    } else if (typeof titleRaw === 'string') {
      titleString = titleRaw;
    } else {
      return { title: 'Customer Testimonials', subtext: null };
    }
    
    // Normalize HTML entities
    titleString = titleString
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
    
    // Split by <br> tag
    const brRegex = /<br\s*\/?>|<\/br\s*>/i;
    const parts = titleString.split(brRegex);
    
    if (parts.length > 1) {
      return {
        title: parts[0]?.trim() || titleString,
        subtext: parts.slice(1).join(' ').trim() || null,
      };
    }
    
    return {
      title: titleString.trim() || 'Customer Testimonials',
      subtext: null,
    };
  }, [titleRaw]);

  if (!items || items.length === 0) {
    return null;
  }

  const renderCard = (item: ClipCardItem, index: number) => {
    if (!item || !item.imageUrl) {
      return null;
    }
    const fallbackTitle = item.title || '';
    const nameFromTitle = fallbackTitle.split(',')[0]?.trim() || item.name || '';
    const ageFromTitle = fallbackTitle.split(',')[1]?.trim() || (item.age ? String(item.age) : '');
    const testimonial = item.testimonial || item.description || '';
    const displayName = item.name || nameFromTitle || 'Customer';
    const displayAge = item.age || (ageFromTitle ? Number(ageFromTitle) || ageFromTitle : '');

    const eventName = `${sectionKey}_${displayName.replace(/\s/g, '')}`;
    const link = item.link || '#';

    const CardLink =
      link && link !== '#'
        ? (
            <AppLink
              to={link}
              className="absolute inset-0 z-10 ga-event-trigger"
              data-eventname={eventName}
              data-pos={priority}
              data-sectionname={eventLabel}
              aria-label={`${displayName} testimonial`}
              shouldRefresh={item.shouldRefresh}
            >
              <span className="sr-only">View {displayName} testimonial</span>
            </AppLink>
          )
        : null;

    const rotation = item.rotation || (index % 2 === 0 ? '2deg' : '-2deg');
    
    return (
      <div
        key={`${item.name || index}-${index}`}
        className="flex-shrink-0 relative"
      >
        <div 
          className="bg-repeat-x pt-[50px] min-[501px]:pt-[70px] min-[701px]:pt-[100px] pb-[40px] md:pt-[65px] md:px-[40px] md:pb-[25px]"
          style={{
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path d="M0 10 C20 5, 40 15, 60 10 S80 5, 100 10 S120 15, 140 10 S160 5, 180 10 S200 15, 220 10" stroke="rgba(0,0,0,0.1)" stroke-width=".3" fill="none"/></svg>')`,
            backgroundRepeat: 'repeat-x',
            backgroundSize: '333% auto',
            backgroundPosition: '0 -5px'
          } as React.CSSProperties}
        >
            {/* Polaroid-style Card */}
            <div
              className="relative bg-[#FFE5E8] shadow-lg rounded mx-[40px] p-[15px] md:mx-0 w-[50vw] md:w-[300px]"
              style={{ 
                transform: `rotate(${rotation})`,
                borderRadius: '4px',
              }}
            >
              {/* Card Image - 427:373 aspect ratio */}
              <div className="w-full" style={{ aspectRatio: '427 / 373' }}>
                {blankImageUrl && item.lazyload ? (
                  <LazyImage
                    src={blankImageUrl}
                    dataSrc={item.imageUrl}
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    className="w-full h-full object-cover"
                    src={resolveImageUrl(item.imageUrl)}
                    alt={displayName}
                    loading={item.lazyload ? 'lazy' : 'eager'}
                  />
                )}
              </div>

              {/* Text area - bottom portion */}
              <div className="pt-3 relative z-20">
                {/* Name and Age */}
                <div className="my-1">
                  <h3 className="text-[0.85rem] font-normal text-[#272B64]" style={{ fontFamily: 'Mulish, sans-serif' }}>
                    {displayName}{displayAge ? `, ${displayAge}` : ''}
                  </h3>
                </div>

                {/* Testimonial - full text, no ellipsis */}
                {testimonial && (
                  <p className="text-[#ACACAC] text-[0.75rem] leading-[16px]">
                    {testimonial}
                  </p>
                )}
              </div>

              {/* Paperclip Icon */}
              {item.clipImageUrl && (
                <div 
                  className="absolute z-20"
                  style={{
                    top: '-20px',
                    // Fixed size so the clip doesn't scale with card width
                    width: '30px',
                    '--clip-position': item.clipPosition || '50%',
                    '--clip-rotation': item.clipRotation || '0deg',
                    left: 'var(--clip-position)',
                    // Center the fixed-width clip at the requested position
                    transform: 'translateX(-50%) rotate(var(--clip-rotation))',
                  } as React.CSSProperties}
                >
                  <img
                    src={resolveImageUrl(item.clipImageUrl)}
                    alt=""
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Link Overlay */}
              {CardLink}
            </div>
            </div>
      </div>
    );
  };

  return (
    <section
      className={[
        // Keep the section itself "in place" so transforms don't create page-level horizontal scroll.
        // Animate an inner wrapper instead (see below).
        'clip-cards-section w-full py-8 md:py-12 relative md:overflow-x-hidden',
      ].join(' ')}
    >
      <div
        className={[
          'transform-gpu',
          'transition-transform duration-[2000ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          'motion-reduce:transition-none motion-reduce:transform-none',
          'md:translate-x-1/2 md:group-data-[inview=true]:translate-x-0',
        ].join(' ')}
      >
        {/* Header */}
        <div className="container-fluid mb-6">
          <h2 className="text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
            <span className="font-primary text-[#9D7792] md:text-[#032C5E]">{titleParts.title}</span>
          </h2>
          {(titleParts.subtext || hashtag) && (
            <p className="text-sm md:text-base font-primary text-[#9D7792] text-center">
              {titleParts.subtext || hashtag}
            </p>
          )}
        </div>

        {/* White Panel with Wavy Top Border */}
        <div className="relative bg-white overflow-visible">
          {/* Wavy Top Border */}
          <div className="absolute top-0 left-0 right-0 h-12 overflow-hidden pointer-events-none">
            <svg
              className="w-full h-full block"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
            >
              <path
                d="M0,60 Q200,20 400,60 T800,60 T1200,60 L1200,120 L0,120 Z"
                fill="white"
              />
            </svg>
          </div>

          {/* Mobile: Horizontal Scroll */}
          <div
            className="md:hidden w-full overflow-x-auto overflow-y-hidden"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#FFE5E8 transparent',
            }}
          >
            <div className="flex min-w-max">
              {items
                .map((item, index) => {
                  const card = renderCard(item, index);
                  return card;
                })
                .filter(Boolean)}
            </div>
          </div>

          {/* Desktop: Horizontal Scroll */}
          <div
            className="hidden md:block w-full overflow-x-auto overflow-y-hidden py-8"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#FFE5E8 transparent',
            }}
          >
            <div className="flex min-w-max">
              {items
                .map((item, index) => {
                  const card = renderCard(item, index);
                  return card;
                })
                .filter(Boolean)}
            </div>
          </div>

          {/* Custom scrollbar styling for webkit browsers */}
          <style>{`
            .clip-cards-section div::-webkit-scrollbar {
              height: 8px;
            }
            .clip-cards-section div::-webkit-scrollbar-track {
              background: transparent;
            }
            .clip-cards-section div::-webkit-scrollbar-thumb {
              background-color: #FFE5E8;
              border-radius: 4px;
            }
            .clip-cards-section div::-webkit-scrollbar-thumb:hover {
              background-color: #FFD1D6;
            }
          `}</style>
        </div>
      </div>
    </section>
  );
}

